import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import ProductCard from '../components/ProductCard';
import { getCmsEntryBySlug, getCmsCollection } from '../lib/cms';
import { imageUrl } from '../lib/images';
import { useCart, getProductPrice } from '../context/CartContext';

export default function ProductDetailPage({ defaultSlug }) {
  const params = useParams();
  const slug = defaultSlug || params.slug;
  const [product, setProduct] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('beskrivelse');
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState('');

  const { addToCart, openCart } = useCart();

  useEffect(() => {
    let isMounted = true;
    async function loadProduct() {
      try {
        setLoading(true);
        // Load product by slug
        const data = await getCmsEntryBySlug(slug);

        if (isMounted) {
          if (data) {
            setProduct(data);
            setActiveImageIndex(0);
            setQuantity(1);
            let defaultV = data.variant_stoerrelse || 'Standard';
            if (defaultV.includes('5 kg og 15 kg') || defaultV.includes('4 og 15 kg')) {
              defaultV = '15 kg';
            }
            setSelectedVariant(defaultV);

            // Load related products
            const isCatItem =
              data.kategori === 'kat' ||
              data.terms?.produktkategori?.includes('kat') ||
              data.terms?.dyreart?.includes('kat') ||
              data.dyretype?.toLowerCase().includes('kat') ||
              slug === 'kromix-katteknas';
            const relatedRes = await getCmsCollection('produkter', { limit: 16 });

            if (isMounted) {
              const currentId = data.id;
              const allItems = (relatedRes.items || []).filter((item) => item.id !== currentId);
              
              if (isCatItem) {
                // For cat products, prioritize other cat items (Avant Cat, Lakseolie)
                const catMatches = allItems.filter((item) =>
                  item.dyretype?.toLowerCase().includes('kat') ||
                  item.kategori === 'kat' ||
                  item.terms?.produktkategori?.includes('kat') ||
                  item.terms?.dyreart?.includes('kat') ||
                  item.navn?.toLowerCase().includes('cat')
                );
                const otherItems = allItems.filter((item) => !catMatches.includes(item));
                setRelatedProducts([...catMatches, ...otherItems].slice(0, 3));
              } else {
                // For dog products, prioritize dog food items
                const dogMatches = allItems.filter((item) =>
                  item.kategori === 'hund' ||
                  item.dyretype?.toLowerCase().includes('hund')
                );
                const otherItems = allItems.filter((item) => !dogMatches.includes(item));
                setRelatedProducts([...dogMatches, ...otherItems].slice(0, 3));
              }
            }
          } else {
            setProduct(null);
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Fejl ved indlæsning af produkt:', err);
        if (isMounted) setLoading(false);
      }
    }

    loadProduct();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="py-32 text-center text-sm text-[#39463A]/80">
        <i className="fa-solid fa-spinner fa-spin text-3xl text-[#71806A] mb-3 block" />
        Indlæser produkt...
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <i className="fa-solid fa-circle-exclamation text-4xl text-[#B98262] mb-4" />
        <h1 className="text-2xl font-bold text-[#39463A]">Produktet blev ikke fundet</h1>
        <p className="text-sm text-[#39463A]/80 mt-2 mb-6">
          Vi kunne ikke finde det efterspurgte produkt i vores katalog.
        </p>
        <Link
          to="/hundefoder"
          className="px-6 py-3 rounded-xl bg-[#71806A] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#5C6A56] transition-colors"
        >
          Se alle produkter
        </Link>
      </div>
    );
  }

  const isCat = product.kategori === 'kat' || product.dyretype?.toLowerCase().includes('kat') || slug === 'kromix-katteknas';
  const isDiverse = product.kategori === 'diverse' || product.dyretype?.toLowerCase().includes('fugl');

  const galleryImages = [
    ...(product.primaert_billede || []),
    ...(product.galleri || []),
  ].filter((v, i, a) => a.indexOf(v) === i); // remove duplicates

  const activeImage = galleryImages[activeImageIndex] || galleryImages[0] || '';

  return (
    <>
      <SeoHead
        title={product.seo_title || `${product.navn} - KROMIX A/S`}
        description={product.meta_description || product.kort_beskrivelse}
        ogImage={activeImage}
      />

      {/* Breadcrumb bar */}
      <div className="bg-[#F4EFE5] border-b border-[#DED3C0] py-3 text-xs text-[#39463A]/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-[#71806A] transition-colors">Forside</Link>
          <span>/</span>
          {isCat ? (
            <Link to="/kat" className="hover:text-[#71806A] transition-colors">Kattefoder</Link>
          ) : isDiverse ? (
            <Link to="/diverse" className="hover:text-[#71806A] transition-colors">Tilbehør</Link>
          ) : (
            <Link to="/hundefoder" className="hover:text-[#71806A] transition-colors">Hundefoder</Link>
          )}
          <span>/</span>
          <span className="font-bold text-[#39463A]">{product.navn}</span>
        </div>
      </div>

      {/* Main Product Showcase Section */}
      <section className="py-10 lg:py-14 bg-[#F4EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 bg-[#FCFAF5] rounded-3xl p-6 sm:p-8 lg:p-10 border border-[#DED3C0] shadow-sm">
            {/* Left: Product Images */}
            <div className="lg:col-span-6 flex flex-col items-center">
              {/* Main Image Stage on Warm Oat */}
              <div className="w-full bg-[#F4EFE5] rounded-2xl p-6 sm:p-10 flex items-center justify-center min-h-[380px] sm:min-h-[440px] border border-[#DED3C0] relative">
                {product.egenskaber && product.egenskaber.includes('Glutenfri') ? (
                  <span className="absolute top-4 left-4 px-3 py-1 text-xs font-bold tracking-wider uppercase rounded-md bg-[#B98262] text-[#FCFAF5] shadow-xs">
                    Glutenfri
                  </span>
                ) : isCat ? (
                  <span className="absolute top-4 left-4 px-3 py-1 text-xs font-bold tracking-wider uppercase rounded-md bg-[#71806A] text-[#FCFAF5] shadow-xs">
                    Fuldfoder kat
                  </span>
                ) : null}

                {activeImage ? (
                  <img
                    src={imageUrl(activeImage, 'width=800,quality=90,format=auto')}
                    alt={product.navn}
                    className="max-h-[360px] w-auto object-contain transition-all duration-300"
                  />
                ) : (
                  <i
                    className={isCat ? 'fa-light fa-cat text-6xl text-[#71806A]/40' : 'fa-light fa-paw text-6xl text-[#71806A]/40'}
                    aria-hidden="true"
                  />
                )}
              </div>

              {/* Gallery Thumbnails */}
              {galleryImages.length > 1 && (
                <div className="flex flex-wrap gap-3 mt-4 justify-center">
                  {galleryImages.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className={`w-20 h-20 rounded-xl overflow-hidden p-2 bg-[#F4EFE5] border transition-all cursor-pointer ${
                        activeImageIndex === idx
                          ? 'border-2 border-[#71806A] shadow-sm scale-105'
                          : 'border-[#DED3C0] hover:border-[#71806A] opacity-75 hover:opacity-100'
                      }`}
                      aria-label={`Vis billede ${idx + 1}`}
                    >
                      <img
                        src={imageUrl(imgUrl, 'width=160,quality=80,format=auto')}
                        alt={`Miniature ${idx + 1}`}
                        className="w-full h-full object-contain"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Product Details & CTAs */}
            <div className="lg:col-span-6 flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                {/* Category & Species: Sage Olive accent */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                    {product.produkttype || 'Kvalitetsfoder'}
                  </span>
                  <span className="text-xs text-[#DED3C0]">•</span>
                  <span className="text-xs font-semibold text-[#39463A]/80 flex items-center gap-1">
                    <i
                      className={isCat ? 'fa-solid fa-cat text-[11px]' : 'fa-solid fa-dog text-[11px]'}
                      aria-hidden="true"
                    />
                    <span>{product.dyretype || (isCat ? 'Kat' : 'Hund')}</span>
                  </span>
                </div>

                {/* Product Name in Deep Moss */}
                <h1 className="text-3xl sm:text-4xl font-extrabold text-[#39463A] tracking-tight">
                  {product.navn}
                </h1>

                {/* Short Description */}
                {product.kort_beskrivelse && (
                  <p className="text-sm sm:text-base text-[#39463A]/85 leading-relaxed">
                    {product.kort_beskrivelse}
                  </p>
                )}

                {/* Sækkestørrelse & Varianter */}
                {product.variant_stoerrelse && (
                  <div className="p-4 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] space-y-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#71806A]">
                      Emballagestørrelse / Variant:
                    </span>
                    <div className="flex flex-wrap gap-2 items-center">
                      {product.variant_stoerrelse.includes('5 kg og 15 kg') ? (
                        <>
                          {['15 kg', '5 kg'].map((v) => (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setSelectedVariant(v)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                selectedVariant === v
                                  ? 'bg-[#39463A] text-white shadow-xs'
                                  : 'bg-white text-[#39463A] border border-[#DED3C0] hover:border-[#71806A]'
                              }`}
                            >
                              {v}
                            </button>
                          ))}
                        </>
                      ) : product.variant_stoerrelse.includes('4 og 15 kg') ? (
                        <>
                          {['15 kg', '4 kg'].map((v) => (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setSelectedVariant(v)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                selectedVariant === v
                                  ? 'bg-[#39463A] text-white shadow-xs'
                                  : 'bg-white text-[#39463A] border border-[#DED3C0] hover:border-[#71806A]'
                              }`}
                            >
                              {v}
                            </button>
                          ))}
                        </>
                      ) : (
                        <p className="text-sm font-bold text-[#39463A] flex items-center gap-2">
                          <i className="fa-solid fa-box text-[#71806A]" aria-hidden="true" />
                          <span>{product.variant_stoerrelse}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Egenskaber pills: Soft Scandinavian pills */}
                {product.egenskaber && (
                  <div className="space-y-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#71806A] block">
                      Fremhævede egenskaber:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {product.egenskaber.split(',').map((prop, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 rounded-lg bg-[#DED3C0]/40 text-[#39463A] border border-[#DED3C0] text-xs font-semibold"
                        >
                          {prop.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Webshop Ordering & Conversion Block */}
              <div className="pt-6 border-t border-[#DED3C0] space-y-4">
                {/* Price Display */}
                <div className="flex items-baseline justify-between bg-[#F4EFE5] p-4 rounded-2xl border border-[#DED3C0]">
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#71806A] block">
                      Vejledende pris:
                    </span>
                    <span className="text-xs text-[#39463A]/70">
                      Webshop uden kortbetaling – afregnes efterfølgende
                    </span>
                  </div>
                  <div className="text-right">
                    {getProductPrice(product, selectedVariant) > 0 ? (
                      <span className="text-2xl sm:text-3xl font-extrabold text-[#39463A]">
                        {getProductPrice(product, selectedVariant) * quantity} kr.
                      </span>
                    ) : (
                      <span className="text-sm font-bold text-[#71806A]">
                        Pris på forespørgsel
                      </span>
                    )}
                  </div>
                </div>

                {/* Quantity and Add to Cart Button */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Quantity selector */}
                  <div className="inline-flex items-center justify-between rounded-xl bg-white border border-[#DED3C0] p-1 shrink-0 h-[48px]">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-full flex items-center justify-center text-[#39463A] hover:bg-[#F4EFE5] rounded-lg text-sm transition-colors cursor-pointer"
                      aria-label="Formindsk antal"
                    >
                      <i className="fa-solid fa-minus text-xs" aria-hidden="true" />
                    </button>
                    <span className="w-12 text-center text-sm font-extrabold text-[#39463A]">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-10 h-full flex items-center justify-center text-[#39463A] hover:bg-[#F4EFE5] rounded-lg text-sm transition-colors cursor-pointer"
                      aria-label="Forøg antal"
                    >
                      <i className="fa-solid fa-plus text-xs" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Primary Add to Cart button */}
                  <button
                    type="button"
                    onClick={() => {
                      const p = getProductPrice(product, selectedVariant);
                      addToCart(product, quantity, selectedVariant || product.variant_stoerrelse, p);
                    }}
                    className="flex-1 h-[48px] px-6 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white font-bold text-xs uppercase tracking-wider transition-all shadow-md hover:shadow-lg active:scale-98 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <i className="fa-solid fa-basket-shopping text-sm" aria-hidden="true" />
                    <span>Læg i kurv ({quantity} stk.)</span>
                  </button>
                </div>

                {/* Secondary Help / Contact note */}
                <div className="flex items-center justify-between text-xs text-[#39463A]/80 pt-1 px-1">
                  <span className="flex items-center gap-1.5">
                    <i className="fa-solid fa-truck-fast text-[#71806A]" aria-hidden="true" />
                    <span>Hurtig levering eller afhentning</span>
                  </span>
                  <Link
                    to={`/kontakt?produkt=${encodeURIComponent(product.navn)}`}
                    className="text-[#71806A] hover:text-[#39463A] font-semibold underline transition-colors"
                  >
                    Stil spørgsmål til foderet
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Structured Detailed Info Tabs: Warm White container */}
          <div className="mt-12 bg-[#FCFAF5] rounded-3xl p-6 sm:p-10 border border-[#DED3C0] shadow-sm">
            {/* Tabs Header */}
            <div className="flex flex-wrap gap-2 border-b border-[#DED3C0] pb-4">
              <button
                type="button"
                onClick={() => setActiveTab('beskrivelse')}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${
                  activeTab === 'beskrivelse'
                    ? 'bg-[#71806A] text-[#FCFAF5] shadow-xs'
                    : 'bg-[#F4EFE5] text-[#39463A] hover:bg-[#DED3C0]/50 border border-[#DED3C0]'
                }`}
              >
                Produktbeskrivelse
              </button>
              {product.ingredienser && (
                <button
                  type="button"
                  onClick={() => setActiveTab('ingredienser')}
                  className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${
                    activeTab === 'ingredienser'
                      ? 'bg-[#71806A] text-[#FCFAF5] shadow-xs'
                      : 'bg-[#F4EFE5] text-[#39463A] hover:bg-[#DED3C0]/50 border border-[#DED3C0]'
                  }`}
                >
                  Ingredienser & Indhold
                </button>
              )}
              {product.analytiske_bestanddele && (
                <button
                  type="button"
                  onClick={() => setActiveTab('analytisk')}
                  className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${
                    activeTab === 'analytisk'
                      ? 'bg-[#71806A] text-[#FCFAF5] shadow-xs'
                      : 'bg-[#F4EFE5] text-[#39463A] hover:bg-[#DED3C0]/50 border border-[#DED3C0]'
                  }`}
                >
                  Analytiske bestanddele
                </button>
              )}
              {product.foderanbefaling && (
                <button
                  type="button"
                  onClick={() => setActiveTab('fodervejledning')}
                  className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors ${
                    activeTab === 'fodervejledning'
                      ? 'bg-[#71806A] text-[#FCFAF5] shadow-xs'
                      : 'bg-[#F4EFE5] text-[#39463A] hover:bg-[#DED3C0]/50 border border-[#DED3C0]'
                  }`}
                >
                  Fodervejledning
                </button>
              )}
            </div>

            {/* Tab Contents */}
            <div className="py-6 text-sm text-[#39463A]/85 leading-relaxed">
              {activeTab === 'beskrivelse' && (
                <div className="space-y-4 max-w-3xl">
                  {product.lang_beskrivelse ? (
                    <div
                      className="prose prose-stone max-w-none text-[#39463A]/85 space-y-3"
                      dangerouslySetInnerHTML={{ __html: product.lang_beskrivelse }}
                    />
                  ) : (
                    <p>{product.kort_beskrivelse}</p>
                  )}
                </div>
              )}

              {activeTab === 'ingredienser' && product.ingredienser && (
                <div className="space-y-4 max-w-3xl">
                  <h3 className="text-base font-bold text-[#39463A]">Råvarer & Sammensætning</h3>
                  <div
                    className="prose prose-stone max-w-none text-[#39463A]/85 bg-[#F4EFE5] p-6 rounded-2xl border border-[#DED3C0]"
                    dangerouslySetInnerHTML={{ __html: product.ingredienser }}
                  />
                  <p className="text-xs text-[#39463A]/70">
                    KROMIX anvender udelukkende råvarer af høj kvalitet. Der tilsættes ingen kunstige aromaer, smags- eller farvestoffer.
                  </p>
                </div>
              )}

              {activeTab === 'analytisk' && product.analytiske_bestanddele && (
                <div className="space-y-4 max-w-3xl">
                  <h3 className="text-base font-bold text-[#39463A]">Analytiske bestanddele & Sporstoffer</h3>
                  <div
                    className="prose prose-stone max-w-none text-[#39463A]/85 bg-[#F4EFE5] p-6 rounded-2xl border border-[#DED3C0] overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_th]:text-left [&_th]:py-2.5 [&_th]:px-4 [&_th]:bg-[#39463A] [&_th]:text-white [&_th]:font-bold [&_th]:text-xs [&_th]:rounded-t-lg [&_td]:py-2 [&_td]:px-4 [&_td]:text-xs [&_td]:border-b [&_td]:border-[#DED3C0] [&_tr:hover]:bg-[#DED3C0]/30 [&_h4]:text-sm [&_h4]:font-bold [&_h4]:text-[#39463A] [&_h4]:mt-6 [&_h4]:mb-2 [&_h4:first-child]:mt-0"
                    dangerouslySetInnerHTML={{ __html: product.analytiske_bestanddele }}
                  />
                </div>
              )}

              {activeTab === 'fodervejledning' && product.foderanbefaling && (
                <div className="space-y-4 max-w-3xl">
                  <h3 className="text-base font-bold text-[#39463A]">Fodervejledning & Dosering</h3>
                  <div
                    className="prose prose-stone max-w-none text-[#39463A]/85 bg-[#F4EFE5] p-6 rounded-2xl border border-[#DED3C0] overflow-x-auto [&_ul]:space-y-1.5 [&_li]:text-xs [&_table]:w-full [&_table]:border-collapse [&_th]:bg-[#39463A] [&_th]:text-white [&_th]:py-2 [&_th]:px-4 [&_td]:py-2 [&_td]:px-4 [&_td]:border-b [&_td]:border-[#DED3C0]"
                    dangerouslySetInnerHTML={{ __html: product.foderanbefaling }}
                  />
                  <div className="p-4 rounded-xl bg-[#DED3C0]/40 border border-[#DED3C0] text-xs text-[#39463A]">
                    <strong className="block mb-1">Vigtigt ved fodring:</strong>
                    {isCat
                      ? 'Mængderne er vejledende og skal altid tilpasses kattens faktiske aktivitetsniveau, alder og foderstand. Sørg altid for, at der er adgang til rigeligt frisk drikkevand.'
                      : 'Mængderne er vejledende og skal altid tilpasses hundens faktiske aktivitetsniveau og foderstand. Sørg altid for, at der er adgang til rigeligt frisk drikkevand.'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-16">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                    {isCat ? 'Flere produkter til kat' : 'Flere foderblandinger'}
                  </span>
                  <h2 className="text-2xl font-extrabold text-[#39463A]">
                    {isCat ? 'Andre produkter til katte' : 'Andre produkter fra KROMIX'}
                  </h2>
                </div>
                <Link
                  to={isCat ? '/diverse' : '/hundefoder'}
                  className="text-xs font-bold uppercase tracking-wider text-[#71806A] hover:text-[#39463A] flex items-center gap-1.5"
                >
                  <span>Se alle</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedProducts.map((rel) => (
                  <ProductCard key={rel.id} product={rel} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
